import { parseLabeledRows } from "@/lib/tool-kit/charts/parse";
import { niceTicks } from "@/lib/tool-kit/charts/scale";
import { estimateTextWidth } from "@/lib/tool-kit/charts/text";

/**
 * The BCG growth-share matrix: market growth rate up the side, relative market share
 * along the bottom on a logarithmic scale with the leader on the left, bubble area
 * proportional to revenue.
 *
 * Relative market share is a product's share divided by the share of its largest
 * competitor, so 1.0 means level with the leader of the rest of the market and 2.0 means
 * twice its size. The cut-offs that split the chart into quadrants — 1.0 for share, 10%
 * a year for growth — are conventions taught with the matrix; Henderson's 1970 essay
 * gives 10% as the usual growth cut-off and treats 1:1 as the share example. Both are
 * adjustable here because a slow market's "high growth" is not 10%.
 */

export type BcgMode = "relative" | "shares";

export type Quadrant = "star" | "question" | "cow" | "dog";

export const QUADRANTS: readonly Quadrant[] = [
  "star",
  "question",
  "cow",
  "dog",
];

export const DEFAULT_SHARE_LINE = 1;
export const DEFAULT_GROWTH_LINE = 10;

export interface BcgRow {
  name: string;
  /** Null when the revenue cell was blank; the bubble is then drawn at minimum size. */
  revenue: number | null;
  /** Market growth in percent per year; may be negative. */
  growth: number;
  /** The product's share divided by its largest competitor's. Always above 0. */
  relativeShare: number;
}

export type RowIssue = "missing" | "share" | "revenue";

export interface ParsedBcg {
  rows: BcgRow[];
  issues: { line: number; reason: RowIssue }[];
}

/**
 * The relative share in a row of cells: the cell itself, or your share over the rival's.
 * Null when a cell is missing; 0 or less when the share is unusable.
 */
function relativeShareOf(
  cells: readonly (number | null)[],
  mode: BcgMode
): number | null {
  const [first, second] = cells;
  if (mode === "relative") {
    return first ?? null;
  }
  if (first === null || first === undefined || !second) {
    return second === 0 ? 0 : null;
  }
  return first / second;
}

function rowIssue(row: BcgRow | null, share: number | null): RowIssue | null {
  if (share === null || row === null) {
    return "missing";
  }
  if (!(Number.isFinite(share) && share > 0)) {
    return "share";
  }
  return row.revenue !== null && row.revenue < 0 ? "revenue" : null;
}

/** Rows of `name, revenue, growth %, relative share` or `…, your share %, rival share %`. */
export function parseBcgRows(text: string, mode: BcgMode): ParsedBcg {
  const rows: BcgRow[] = [];
  const issues: ParsedBcg["issues"] = [];
  for (const row of parseLabeledRows(text).rows) {
    const [revenue, growth, ...shares] = row.values;
    const share = relativeShareOf(shares, mode);
    const candidate: BcgRow | null =
      row.label === "" || growth === null || growth === undefined
        ? null
        : {
            name: row.label,
            revenue: revenue ?? null,
            growth,
            relativeShare: share ?? 0,
          };
    const issue = rowIssue(candidate, share);
    if (issue === null && candidate) {
      rows.push(candidate);
    } else if (issue) {
      issues.push({ line: row.line, reason: issue });
    }
  }
  return { rows, issues };
}

/** High share is at or above the share line; high growth is at or above the growth line. */
export function quadrantOf(
  row: Pick<BcgRow, "growth" | "relativeShare">,
  shareLine: number,
  growthLine: number
): Quadrant {
  const highShare = row.relativeShare >= shareLine;
  const highGrowth = row.growth >= growthLine;
  if (highShare) {
    return highGrowth ? "star" : "cow";
  }
  return highGrowth ? "question" : "dog";
}

export interface QuadrantSummary {
  quadrant: Quadrant;
  count: number;
  revenue: number;
  /** Share of total revenue, 0–1; 0 when there is no revenue. */
  revenueShare: number;
}

export function summarizeQuadrants(
  rows: readonly BcgRow[],
  shareLine: number,
  growthLine: number
): QuadrantSummary[] {
  const total = rows.reduce((sum, row) => sum + (row.revenue ?? 0), 0);
  return QUADRANTS.map((quadrant) => {
    const inQuadrant = rows.filter(
      (row) => quadrantOf(row, shareLine, growthLine) === quadrant
    );
    const revenue = inQuadrant.reduce(
      (sum, row) => sum + (row.revenue ?? 0),
      0
    );
    return {
      quadrant,
      count: inQuadrant.length,
      revenue,
      revenueShare: total > 0 ? revenue / total : 0,
    };
  });
}

/* ---------------------------------------------------------------- layout */

export const BCG_WIDTH = 760;
export const BCG_HEIGHT = 580;
const PLOT = {
  left: 78,
  top: 64,
  right: BCG_WIDTH - 28,
  bottom: BCG_HEIGHT - 84,
};
/** Radius of the largest bubble; every other radius scales with √revenue, so area ∝ revenue. */
export const R_MAX = 46;
/** Below this a bubble would vanish, so a very small product is drawn at this size. */
export const R_FLOOR = 4;
/** Radius when no revenue was given. */
export const R_NO_REVENUE = 8;
const LABEL_SIZE = 11;
const LABEL_GAP = 4;

export interface Bubble {
  index: number;
  row: BcgRow;
  quadrant: Quadrant;
  x: number;
  y: number;
  r: number;
  label: {
    x: number;
    y: number;
    anchor: "start" | "middle" | "end";
    text: string;
  };
}

export interface BcgLayout {
  plot: { left: number; top: number; right: number; bottom: number };
  bubbles: Bubble[];
  xTicks: { value: number; x: number }[];
  yTicks: { value: number; y: number }[];
  /** Pixel position of the share line and the growth line. */
  shareX: number;
  growthY: number;
}

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function overlaps(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
}

const LOG_STEPS = [1, 2, 5] as const;

/** Whole decades around the data, always including 0.1× to 10× so the chart looks familiar. */
export function shareDomain(shares: readonly number[]): [number, number] {
  const low = Math.min(0.1, ...shares);
  const high = Math.max(10, ...shares);
  return [
    10 ** Math.floor(Math.log10(low) + 1e-9),
    10 ** Math.ceil(Math.log10(high) - 1e-9),
  ];
}

function shareTicks(min: number, max: number): number[] {
  const ticks: number[] = [];
  for (
    let exponent = Math.floor(Math.log10(min));
    exponent <= Math.ceil(Math.log10(max));
    exponent++
  ) {
    for (const step of LOG_STEPS) {
      const value = Number((step * 10 ** exponent).toPrecision(12));
      if (value >= min * (1 - 1e-9) && value <= max * (1 + 1e-9)) {
        ticks.push(value);
      }
    }
  }
  return ticks;
}

export function layoutBcg(
  rows: readonly BcgRow[],
  shareLine: number,
  growthLine: number
): BcgLayout {
  const [minShare, maxShare] = shareDomain([
    ...rows.map((row) => row.relativeShare),
    shareLine,
  ]);
  const logMin = Math.log10(minShare);
  const logMax = Math.log10(maxShare);
  const plotWidth = PLOT.right - PLOT.left;
  const plotHeight = PLOT.bottom - PLOT.top;
  // Reversed: the biggest share is at the left edge.
  const xOf = (share: number) =>
    PLOT.left + ((logMax - Math.log10(share)) / (logMax - logMin)) * plotWidth;
  const growths = [...rows.map((row) => row.growth), growthLine];
  const axis = niceTicks(Math.min(...growths), Math.max(...growths), {
    count: 6,
  });
  const yOf = (growth: number) =>
    PLOT.bottom - ((growth - axis.min) / (axis.max - axis.min)) * plotHeight;
  const maxRevenue = Math.max(0, ...rows.map((row) => row.revenue ?? 0));
  const radius = (revenue: number | null) =>
    revenue === null || maxRevenue === 0
      ? R_NO_REVENUE
      : Math.max(R_FLOOR, R_MAX * Math.sqrt(revenue / maxRevenue));

  const bubbles: Bubble[] = rows.map((row, index) => {
    const r = radius(row.revenue);
    return {
      index,
      row,
      quadrant: quadrantOf(row, shareLine, growthLine),
      x: xOf(row.relativeShare),
      y: yOf(row.growth),
      r,
      label: { x: 0, y: 0, anchor: "start", text: row.name },
    };
  });
  placeLabels(bubbles);
  return {
    plot: PLOT,
    bubbles,
    xTicks: shareTicks(minShare, maxShare).map((value) => ({
      value,
      x: xOf(value),
    })),
    yTicks: axis.ticks.map((value) => ({ value, y: yOf(value) })),
    shareX: xOf(shareLine),
    growthY: yOf(growthLine),
  };
}

/**
 * Put each name beside its bubble — right, left, above, then below — in the first spot
 * that stays inside the plot and clear of every bubble and every label already placed.
 * Largest bubbles are placed first, since they are the hardest to move around. A name
 * that fits nowhere goes to the right anyway: a crowded chart is still readable from
 * the table beneath it.
 */
function placeLabels(bubbles: Bubble[]): void {
  const taken: Box[] = bubbles.map((b) => ({
    x0: b.x - b.r,
    y0: b.y - b.r,
    x1: b.x + b.r,
    y1: b.y + b.r,
  }));
  const order = [...bubbles].sort((a, b) => b.r - a.r);
  for (const bubble of order) {
    const width = estimateTextWidth(bubble.label.text, {
      fontSize: LABEL_SIZE,
    });
    const height = LABEL_SIZE + 2;
    const candidates = [
      {
        x: bubble.x + bubble.r + LABEL_GAP,
        y: bubble.y,
        anchor: "start" as const,
        x0: bubble.x + bubble.r + LABEL_GAP,
      },
      {
        x: bubble.x - bubble.r - LABEL_GAP,
        y: bubble.y,
        anchor: "end" as const,
        x0: bubble.x - bubble.r - LABEL_GAP - width,
      },
      {
        x: bubble.x,
        y: bubble.y - bubble.r - LABEL_GAP - height / 2,
        anchor: "middle" as const,
        x0: bubble.x - width / 2,
      },
      {
        x: bubble.x,
        y: bubble.y + bubble.r + LABEL_GAP + height / 2,
        anchor: "middle" as const,
        x0: bubble.x - width / 2,
      },
    ];
    const boxOf = (c: (typeof candidates)[number]): Box => ({
      x0: c.x0,
      y0: c.y - height / 2,
      x1: c.x0 + width,
      y1: c.y + height / 2,
    });
    const fits = (c: (typeof candidates)[number]) => {
      const box = boxOf(c);
      const inside =
        box.x0 >= PLOT.left &&
        box.x1 <= PLOT.right &&
        box.y0 >= PLOT.top &&
        box.y1 <= PLOT.bottom;
      // The bubble's own box is in `taken`, so ignore that one.
      const own = taken[bubble.index];
      return (
        inside && !taken.some((other) => other !== own && overlaps(box, other))
      );
    };
    const chosen = candidates.find(fits) ?? candidates[0];
    bubble.label = {
      x: chosen.x,
      y: chosen.y,
      anchor: chosen.anchor,
      text: bubble.label.text,
    };
    taken.push(boxOf(chosen));
  }
}
